import * as bcrypt from 'bcrypt';
import { ForbiddenException, Inject, Injectable } from '@nestjs/common';
import { AuthenticatedUserResponse } from '@ip6m/server/shared';
import { ConfigurationService } from '@ip6m/server/feature-configuration';
import {
  UserDataService,
  UserEntity,
  UserIncompleteException,
} from '@ip6m/server/data-access-user';
import { TokenService } from '@ip6m/server/auth';
import { BCRYPT_SALT_ROUNDS } from '@ip6m/server/config';

@Injectable()
export class LoginAndRegistrationService {
  constructor(
    private configService: ConfigurationService,
    private userService: UserDataService,
    private tokenService: TokenService,
    @Inject(BCRYPT_SALT_ROUNDS) private bcryptSaltRounds: number
  ) {}

  /**
   * @throws UserIncompleteException
   * @throws UserAlreadyExistsException
   * @throws ForbiddenException
   */
  public async register(
    userData: Omit<UserEntity, 'id' | 'isActive'>
  ): Promise<AuthenticatedUserResponse> {
    if (!await this.configService.isUserRegisterEnabled()) {
      throw new ForbiddenException('User registration disabled.');
    }

    if (!userData.password) {
      throw new UserIncompleteException('missing password');
    }

    const password = await bcrypt.hash(
      userData.password,
      this.bcryptSaltRounds
    );

    const userEntity = await this.userService.create({
      ...userData,
      password,
    });

    const idToken = this.tokenService.idTokenFromUser(userEntity);
    const accessToken = this.tokenService.accessTokenFromUserId(userEntity.id);

    return new AuthenticatedUserResponse({ idToken, accessToken });
  }

  /**
   * @throws ForbiddenException
   */
  public async login(
    email: string,
    password: string
  ): Promise<AuthenticatedUserResponse> {
    const [userEntity] = await this.userService.findByEmail(email);
    if (!userEntity) {
      throw new ForbiddenException();
    }

    const isPassValid = await bcrypt.compare(password, userEntity.password);
    if (!isPassValid) {
      throw new ForbiddenException();
    }

    const idToken = this.tokenService.idTokenFromUser(userEntity);
    const accessToken = this.tokenService.accessTokenFromUserId(userEntity.id);

    return new AuthenticatedUserResponse({ idToken, accessToken });
  }
}
